import React, { useEffect, useState } from 'react';
import { useList } from '@refinedev/core';
import {
  Card,
  Col,
  Row,
  Statistic,
  List,
  Typography,
  Button,
  Empty,
  Tag,
  Space,
  Divider,
} from 'antd';
import {
  CloudOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  UserOutlined,
  ClusterOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { Sandbox } from '../../types/sandbox';
import { StatusBadge } from '../../components/sandboxes/StatusBadge';
import { StatusDistributionChart } from '../../components/charts/StatusDistributionChart';
import { SandboxTimelineChart } from '../../components/charts/SandboxTimelineChart';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';

const { Title, Text } = Typography;

export const DashboardPage: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const { data: sandboxes, isLoading, refetch } = useList<Sandbox>({
    resource: 'sandboxes',
    queryOptions: {
      refetchInterval: autoRefresh ? 30000 : false, // Auto-refresh every 30 seconds
    },
  });

  const totalSandboxes = sandboxes?.data.length || 0;
  const readySandboxes = sandboxes?.data.filter((s) => s.status === 'ready').length || 0;
  const creatingSandboxes = sandboxes?.data.filter((s) => s.status === 'creating').length || 0;
  const pendingSandboxes = sandboxes?.data.filter((s) => s.status === 'pending').length || 0;
  const failedSandboxes = sandboxes?.data.filter((s) => s.status === 'failed').length || 0;

  // Update last refresh timestamp
  useEffect(() => {
    if (!isLoading) {
      setLastRefresh(new Date());
    }
  }, [isLoading]);

  // Manual refresh
  const handleRefresh = () => {
    refetch();
  };

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  // Calculate cluster distribution
  const clusterCounts = sandboxes?.data.reduce((acc, sandbox) => {
    const cluster = sandbox.spec.cluster;
    acc[cluster] = (acc[cluster] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  // Calculate unique users
  const uniqueUsers = new Set(sandboxes?.data.map((s) => s.created_by) || []).size;

  if (isLoading) {
    return <LoadingSkeleton type="dashboard" />;
  }

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Dashboard
          </Title>
          <Text type="secondary">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={isLoading}
            >
              Refresh
            </Button>
            <Button
              type={autoRefresh ? 'primary' : 'default'}
              onClick={toggleAutoRefresh}
            >
              Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title="Total Sandboxes"
              value={totalSandboxes}
              prefix={<CloudOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title="Ready"
              value={readySandboxes}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title="Creating"
              value={creatingSandboxes}
              prefix={<SyncOutlined spin />}
              valueStyle={{ color: '#1677ff' }}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title="Pending"
              value={pendingSandboxes}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title="Failed"
              value={failedSandboxes}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card>
            <Statistic
              title="Active Users"
              value={uniqueUsers}
              prefix={<UserOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Sandbox Timeline (Last 7 Days)" loading={isLoading}>
            {sandboxes?.data && sandboxes.data.length > 0 ? (
              <SandboxTimelineChart sandboxes={sandboxes.data} days={7} />
            ) : (
              <Empty description="No sandbox data available" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Status Distribution" loading={isLoading}>
            {sandboxes?.data && sandboxes.data.length > 0 ? (
              <StatusDistributionChart sandboxes={sandboxes.data} />
            ) : (
              <Empty description="No sandbox data available" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Activity & Cluster Stats */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title="Recent Sandboxes"
            extra={<a href="/sandboxes">View All</a>}
            loading={isLoading}
          >
            {sandboxes?.data && sandboxes.data.length > 0 ? (
              <List
                dataSource={sandboxes.data.slice(0, 10)}
                renderItem={(sandbox) => (
                  <List.Item
                    actions={[
                      <a key="view" href={`/sandboxes/show/${sandbox.id}`}>
                        View
                      </a>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{sandbox.id}</Text>
                          <StatusBadge status={sandbox.status} />
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text type="secondary">
                            <UserOutlined /> {sandbox.created_by}
                          </Text>
                          <Text type="secondary">
                            <ClusterOutlined /> {sandbox.spec.cluster}
                          </Text>
                          <Text type="secondary">
                            <FieldTimeOutlined />{' '}
                            {new Date(sandbox.created_at).toLocaleString()}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No sandboxes created yet" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Cluster Distribution" loading={isLoading}>
            {Object.keys(clusterCounts).length > 0 ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {Object.entries(clusterCounts).map(([cluster, count]) => (
                  <div key={cluster}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space>
                          <ClusterOutlined />
                          <Text>{cluster}</Text>
                        </Space>
                      </Col>
                      <Col>
                        <Tag color="blue">{count} sandboxes</Tag>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '12px 0' }} />
                  </div>
                ))}

                <Statistic
                  title="Success Rate"
                  value={
                    totalSandboxes > 0
                      ? ((readySandboxes / totalSandboxes) * 100).toFixed(1)
                      : 0
                  }
                  suffix="%"
                  valueStyle={{
                    color: readySandboxes / totalSandboxes > 0.8 ? '#52c41a' : '#faad14',
                  }}
                />
              </Space>
            ) : (
              <Empty description="No cluster data available" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
